/* ──────────────────────────────
   components/BadgeTable.jsx
   ────────────────────────────── */
   import React, { useEffect, useState } from "react";
   import dayjs               from "dayjs";
   import $                   from "jquery";
   import { Link, useNavigate } from "react-router-dom";
   import { useDispatch, useSelector } from "react-redux";
   import Pagination          from "../../pages/Pagination";
   import { Tooltip }         from "@mui/material";
   import AddIcon             from "@mui/icons-material/Add";
   import EditIcon            from "@mui/icons-material/Edit";
   import DeleteForeverIcon   from "@mui/icons-material/DeleteForever";
   import Male                from "../../assets/images/male.png";
   
   import {
     getBadges,
     createBadge,
     updateBadge,
     deleteBadge,
   } from "../../store/badge/action";
   
   const BadgeTable = () => {
     const dispatch = useDispatch();
     const navigate = useNavigate();
   
     /* ───────────── UI state ───────────── */
     const [activePage, setActivePage]   = useState(1);
     const [rowsPerPage, setRowsPerPage] = useState(10);
     const [search, setSearch]           = useState("");
   
     const [showModal, setShowModal]   = useState(false);
     const [isEdit, setIsEdit]         = useState(false);
     const [current, setCurrent]       = useState(null);
   
     const [name, setName]             = useState("");
     const [description, setDescription] = useState("");
     const [file, setFile]             = useState(null);
   
     /* ───────────── Redux state ───────────── */
     const { badges = [] } = useSelector((s) => s.badge || {});
   
     /* ───────────── Effects ───────────── */
     useEffect(() => { dispatch(getBadges()); }, [dispatch]);
   
     /* ensure broken images fall back */
     useEffect(() => {
       $("img").on("error", function () { $(this).attr("src", Male); });
     }, []);
   
     /* ───────────── Modal helpers ───────────── */
     const resetModal = () => {
       setShowModal(false);
       setIsEdit(false);
       setCurrent(null);
       setName("");
       setDescription("");
       setFile(null);
     };
   
     const openCreate = () => { resetModal(); setShowModal(true); };
   
     const openEdit = (badge) => {
       setIsEdit(true);
       setCurrent(badge);
       setName(badge.name);
       setDescription(badge.description || "");
       setShowModal(true);
     };
   
     const submitModal = (e) => {
       e.preventDefault();
       const fd = new FormData();
       fd.append("name", name.trim());
       fd.append("description", description.trim());
       if (file) fd.append("image", file);
   
       isEdit
         ? dispatch(updateBadge(current._id, fd))
         : dispatch(createBadge(fd));
   
       resetModal();
     };
   
     const removeBadge = (id) => {
       if (window.confirm("Delete this badge?")) dispatch(deleteBadge(id));
     };
   
     /* ───────────── Render ───────────── */
     return (
       <>
         {/* ─── Title & breadcrumb ─── */}
         <div className="page-title">
           <div className="row">
             <div className="col-12 col-md-6 order-md-1 order-last">
               <h3 className="mb-3 text-dark">Badges</h3>
             </div>
             <div className="col-12 col-md-6 order-md-2 order-first">
               <nav aria-label="breadcrumb" className="breadcrumb-header float-start float-lg-end">
                 <ol className="breadcrumb">
                   <li className="breadcrumb-item">
                     <Link to="/admin/dashboard" className="text-danger">Dashboard</Link>
                   </li>
                   <li className="breadcrumb-item active" aria-current="page">Badges</li>
                 </ol>
               </nav>
             </div>
           </div>
         </div>
   
         {/* ─── Search & “New” button ─── */}
         <div className="card-header">
           <div className="row my-3">
             <div className="col-md-4 d-flex align-items-center">
               <button className="btn btn-success" onClick={openCreate}>
                 <AddIcon /> New Badge
               </button>
             </div>
             <div className="col-md-8">
               <div className="input-group mb-3 border rounded-pill">
                 <input
                   type="search"
                   placeholder="Search by name or description…"
                   className="form-control bg-none border-0 rounded-pill"
                   value={search}
                   onChange={(e) => setSearch(e.target.value)}
                 />
                 <span className="input-group-text border-0 bg-transparent">
                   <i className="fas fa-search text-danger" />
                 </span>
               </div>
             </div>
           </div>
         </div>
   
         {/* ─── Table ─── */}
         <div className="card-body card-overflow pt-0">
           <table className="table table-striped text-center mt-5">
             <thead>
               <tr>
                 <th>No.</th>
                 <th>Image</th>
                 <th>Name</th>
                 <th>Description</th>
                 <th>Created</th>
                 <th>Updated</th>
                 <th>Action</th>
               </tr>
             </thead>
             <tbody>
               {badges
                 .filter((b) =>
                   (b.name + " " + (b.description || ""))
                     .toLowerCase()
                     .includes(search.trim().toLowerCase())
                 )
                 .map((b, idx) => (
                   <tr key={b._id}>
                     <td>{idx + 1}</td>
   
                     {/* image */}
                     <td>
                       <img
                         src={b.image || Male}
                         alt="badge"
                         height="40"
                         width="40"
                         style={{ objectFit: "cover", borderRadius: 8 }}
                       />
                     </td>
   
                     <td>{b.name}</td>
                     <td style={{ maxWidth: 250, whiteSpace: "normal" }}>
                       {b.description || "—"}
                     </td>
                     <td>{dayjs(b.createdAt).format("DD-MMM-YYYY")}</td>
                     <td>{dayjs(b.updatedAt).format("DD-MMM-YYYY")}</td>
   
                     <td>
                       <Tooltip title="Edit">
                         <button
                           className="btn btn-sm btn-info me-2"
                           onClick={() => openEdit(b)}
                         >
                           <EditIcon />
                         </button>
                       </Tooltip>
                       <Tooltip title="Delete">
                         <button
                           className="btn btn-sm btn-danger"
                           onClick={() => removeBadge(b._id)}
                         >
                           <DeleteForeverIcon />
                         </button>
                       </Tooltip>
                     </td>
                   </tr>
                 ))}
               {badges.length === 0 && (
                 <tr>
                   <td colSpan="7" align="center">
                     Nothing to show!!
                   </td>
                 </tr>
               )}
             </tbody>
           </table>
   
           {/* pagination (front-end) */}
           <Pagination
             activePage={activePage}
             rowsPerPage={rowsPerPage}
             userTotal={badges.length}
             handleRowsPerPage={setRowsPerPage}
             handlePageChange={setActivePage}
           />
         </div>
   
         {/* ─── Create / Edit modal ─── */}
         {showModal && (
           <div className="modal fade show d-block" tabIndex="-1">
             <div className="modal-dialog">
               <form className="modal-content" onSubmit={submitModal}>
                 <div className="modal-header">
                   <h5 className="modal-title">
                     {isEdit ? "Edit Badge" : "Create Badge"}
                   </h5>
                   <button type="button" className="btn-close" onClick={resetModal} />
                 </div>
   
                 <div className="modal-body">
                   <div className="mb-3">
                     <label className="form-label">Name</label>
                     <input
                       className="form-control"
                       required
                       value={name}
                       onChange={(e) => setName(e.target.value)}
                     />
                   </div>
   
                   <div className="mb-3">
                     <label className="form-label">Description (optional)</label>
                     <textarea
                       className="form-control"
                       rows="3"
                       value={description}
                       onChange={(e) => setDescription(e.target.value)}
                     />
                   </div>
   
                   <div className="mb-3">
                     <label className="form-label">
                       Image {isEdit && "(leave empty to keep current)"}
                     </label>
                     <input
                       className="form-control"
                       type="file"
                       accept="image/*"
                       onChange={(e) => setFile(e.target.files[0])}
                     />
                   </div>
                 </div>
   
                 <div className="modal-footer">
                   <button className="btn btn-secondary" type="button" onClick={resetModal}>
                     Cancel
                   </button>
                   <button className="btn btn-primary" type="submit">
                     {isEdit ? "Save Changes" : "Create"}
                   </button>
                 </div>
               </form>
             </div>
           </div>
         )}
       </>
     );
   };
   
   export default BadgeTable;
   
